import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChatBubbleLeftRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { driveClient } from '@/lib/drive/driveClient';
import type { CommentRow, DriveEntry } from '@/lib/drive/driveClient';
import { formatTimestamp } from '@/lib/drive/format';

/**
 * The file's discussion thread, inside the details panel: everyone who can see the
 * drive can comment; the owner moderates. New comments from other people arrive as
 * socket + alert notifications (handled in the vault routes).
 */
export function DriveComments({ entry }: { entry: DriveEntry }) {
  const { t, i18n } = useTranslation();
  const [comments, setComments] = useState<CommentRow[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (entry.kind !== 'file') return;
    try {
      setComments(await driveClient.listComments(entry.path));
    } catch {
      setComments([]);
    }
  }, [entry]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await driveClient.addComment(entry.path, body);
      setDraft('');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (commentId: string) => {
    try {
      await driveClient.deleteComment(commentId);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border-color pt-3">
      <div className="flex items-center gap-2">
        <ChatBubbleLeftRightIcon className="h-4 w-4 flex-shrink-0 text-accent" />
        <p className="whitespace-nowrap text-sm font-medium text-foreground">{t('drive.comments')}</p>
      </div>
      {comments === null ? (
        <p className="text-xs text-muted-foreground">…</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('drive.commentsEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.map((comment) => (
            <li key={comment.id} className="rounded-md border border-border-color px-2.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate whitespace-nowrap text-xs font-medium text-foreground">
                  {comment.userName || t('drive.commentsAnonymous')}
                </p>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <span className="whitespace-nowrap text-[10px] text-muted-foreground">
                    {formatTimestamp(new Date(comment.createdAt ?? 0).getTime(), i18n.resolvedLanguage)}
                  </span>
                  <button
                    type="button"
                    title={t('drive.commentsDelete')}
                    onClick={() => void remove(comment.id)}
                    className="rounded p-0.5 text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <p className="mt-1 break-words text-xs text-foreground">{comment.body}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('drive.commentsPlaceholder')}
          rows={2}
          className="min-h-0 w-full flex-1 resize-none rounded-md border border-border-color bg-background px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground"
        />
        <button
          type="button"
          disabled={busy || !draft.trim()}
          onClick={() => void submit()}
          className="flex-shrink-0 whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {t('drive.commentsSend')}
        </button>
      </div>
    </div>
  );
}
